import React from 'react';
import { H1 } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled, { css } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import { createColumnHelper } from '@tanstack/react-table';
import { CompanyResponse } from '../../api/admin';
import { Table } from '../../components/Table/CompactTable';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Checkbox from '../../components/elements/Checkbox';
import { SmallPortalTooltip, SmallTooltip } from '../../components/Tooltip';
import LearningPlanCell from '../../components/Admin/Cells/LearningPlanCell';
import EditLearnersLearningPlanModal from '../../components/Admin/Modals/EditLearnersLearningPlanModal';
import AddLearnersToCompanyModal from '../../components/Admin/Modals/AddLearnersToCompanyModal';
import InviteLearnersToCohortModal from '../../components/Admin/Modals/InviteLearnersToCohortModal';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import {
  CollapsibleSectionFooter,
  CollapsibleSectionFooterButton,
  CollapsibleSectionLabel,
} from '../../components/Admin/CollapsibleSection';
import {
  FiltersHorizontalWrapper,
  MultiSelectFilterPill,
  TextFilterPill,
} from '../../components/elements/Filter';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../../components/Admin/Progress/Tooltip';
import dayjs from 'dayjs';
import { ROUTES } from '../../routing';
day.extend(utc);

const CheckIcon = styled.i.attrs({
  className: 'fa-solid fa-check',
})`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px;
`;

const CrossIcon = styled.i.attrs({
  className: 'fa-solid fa-xmark',
})`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 16px;
`;

const NavbarWithBreadcrumbs = ({
  name,
  id,
}: {
  name?: string;
  id?: string;
}) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      ...(name && id
        ? [
            {
              label: name,
              url: ROUTES.ADMIN.COMPANY.getPath({ companyId: id }),
            },
          ]
        : []),
      { label: 'Learners', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0px;
  margin-bottom: 72px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

export default function Learners() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));
  const [isInviteToCohortModalOpen, setIsInviteToCohortModalOpen] =
    React.useState(false);
  const [isEditLearningPlanModalOpen, setIsEditLearningPlanModalOpen] =
    React.useState(false);
  const [isAddLearnersModalOpen, setIsAddLearnersModalOpen] =
    React.useState(false);
  const [learnerName, setLearnerName] = React.useState('');
  const [tableSelection, setTableSelection] = React.useState<
    Record<string, boolean>
  >({});
  const [selectedCohorts, setSelectedCohorts] = React.useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>([]);
  const topicOptions = React.useMemo(() => {
    return [
      ...new Set(
        data?.learners?.flatMap(
          (learner) => learner?.learningPlan?.map((plan) => plan.topic) ?? []
        )
      ),
    ];
  }, [data]);

  const filteredLearners = React.useMemo(() => {
    let filtered = data?.learners ?? [];
    if (learnerName.trim()) {
      const search = learnerName.trim().toLowerCase();
      filtered = filtered.filter((learner) =>
        learner.name?.toLowerCase().includes(search)
      );
    }
    if (selectedCohorts.length > 0) {
      filtered = filtered.filter((learner) => {
        const learnersCohortIds = data?.cohorts
          .filter((cohort) =>
            cohort.learners.some((l) => l.learnerId === learner.id)
          )
          .map((cohort) => cohort.cycleId);

        return learnersCohortIds?.some((cohortId) =>
          selectedCohorts.includes(cohortId.toString())
        );
      });
    }
    if (selectedTopics.length > 0) {
      filtered = filtered.filter((learner) =>
        learner?.learningPlan?.some((plan) =>
          selectedTopics.includes(plan.topic)
        )
      );
    }
    return filtered;
  }, [data, learnerName, selectedTopics, selectedCohorts]);

  const selectedLearners = React.useMemo(() => {
    return filteredLearners.filter(
      (learner) => tableSelection[learner.id.toString()]
    );
  }, [filteredLearners, tableSelection]);

  const orderedLearners = React.useMemo(() => {
    return [...filteredLearners].sort((a, b) => {
      const aCohorts = getCohortsForLearner(a.id, data);
      const bCohorts = getCohortsForLearner(b.id, data);
      const aCurrent = aCohorts.some((cohort) => cohort.isOpen);
      const bCurrent = bCohorts.some((cohort) => cohort.isOpen);

      if (aCurrent !== bCurrent) return aCurrent ? -1 : 1;

      const latestStart = (cohorts: typeof aCohorts) =>
        cohorts.reduce(
          (latest, cohort) =>
            Math.max(latest, day(cohort.startDate).utc().valueOf()),
          0
        );
      const cohortOrder = latestStart(bCohorts) - latestStart(aCohorts);
      if (cohortOrder !== 0) return cohortOrder;

      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [filteredLearners, data]);

  const numSelected = selectedLearners.length;

  const shouldVirtualize = filteredLearners.length > 20;

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <NavbarWithBreadcrumbs name={data?.name} id={id} />
      <CompanyPageWrapper
        currentPage={COMPANY_PAGES.LEARNERS}
        shouldAddEmptyDiv={false}
      >
        <H1 style={{ marginTop: 32 }}>{data?.name} Learners</H1>

        <Content style={{ marginBottom: 0 }}>
          <FiltersHorizontalWrapper
            style={{ marginTop: '16px', marginBottom: 16 }}
          >
            <TextFilterPill
              label="Learner"
              value={learnerName}
              placeholder="Type the learner name..."
              onChange={setLearnerName}
            />
            <MultiSelectFilterPill
              label="Cohort"
              values={selectedCohorts}
              options={
                data?.cohorts.map((cohort) => ({
                  label: `${day(cohort.startDate)
                    .utc()
                    .format('MMM DD, YYYY')} - ${day(cohort.endDate)
                    .utc()
                    .format('MMM DD, YYYY')}${
                    cohort.isOpen ? ' (Active)' : ''
                  }`,
                  value: cohort.cycleId.toString(),
                })) ?? []
              }
              onChange={(values) => setSelectedCohorts(values as string[])}
            />
            <MultiSelectFilterPill
              label="Topic"
              values={selectedTopics}
              options={
                topicOptions.map((topic) => ({
                  label: topic,
                  value: topic,
                })) ?? []
              }
              onChange={(values) => setSelectedTopics(values as string[])}
            />
          </FiltersHorizontalWrapper>

          <TableWrapper enableVirtualization={shouldVirtualize}>
            <Table
              data={orderedLearners}
              columns={createColumns(data)}
              getRowId={(row) => row.id.toString()}
              rowSelection={tableSelection}
              onRowSelectionChange={setTableSelection}
              initialPinning={{
                left: ['select', 'name'],
              }}
              shouldPinHeaderRow
              enableVirtualization={shouldVirtualize}
              virtualizationRowHeight={50}
              className="learners-table"
              footer={
                <CollapsibleSectionFooter>
                  <CollapsibleSectionLabel>
                    {filteredLearners.length}{' '}
                    {filteredLearners.length === 1 ? 'Learner' : 'Learners'}
                    {numSelected > 0 && <>. {numSelected} selected</>}
                  </CollapsibleSectionLabel>
                  <Row>
                    <CollapsibleSectionFooterButton
                      label="Add New Learners"
                      faIcon="fa-solid fa-plus"
                      showIconBeforeLabel
                      tooltip="Upload a CSV or add new learners manually."
                      onClick={() => setIsAddLearnersModalOpen(true)}
                    />
                    <CollapsibleSectionFooterButton
                      label="Invite to Cohort"
                      faIcon="fa-solid fa-calendar-circle-user"
                      showIconBeforeLabel
                      tooltip="Select one or more learners and invite them to a module, within a cohort."
                      isDisabled={numSelected === 0}
                      onClick={() => setIsInviteToCohortModalOpen(true)}
                    />
                    <CollapsibleSectionFooterButton
                      label="Edit Learning Plan"
                      faIcon="fa-solid fa-pen-to-square"
                      showIconBeforeLabel
                      tooltip="Select one or more learners and edit their learning plan."
                      isDisabled={numSelected === 0}
                      onClick={() => setIsEditLearningPlanModalOpen(true)}
                    />
                  </Row>
                </CollapsibleSectionFooter>
              }
            />
          </TableWrapper>
        </Content>
      </CompanyPageWrapper>
      <InviteLearnersToCohortModal
        isOpen={isInviteToCohortModalOpen}
        onClose={() => setIsInviteToCohortModalOpen(false)}
        companyId={parseInt(id)}
        selectedLearnerIds={selectedLearners.map(
          (learner) => learner?.id ?? -1
        )}
      />
      <EditLearnersLearningPlanModal
        isOpen={isEditLearningPlanModalOpen}
        onClose={() => setIsEditLearningPlanModalOpen(false)}
        companyId={parseInt(id)}
        selectedLearnerIds={selectedLearners.map(
          (learner) => learner?.id ?? -1
        )}
      />
      <AddLearnersToCompanyModal
        isOpen={isAddLearnersModalOpen}
        onClose={() => setIsAddLearnersModalOpen(false)}
        companyId={parseInt(id)}
      />
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<CompanyResponse['learners'][number]>();

// Helper function to get cohorts for a learner
const getCohortsForLearner = (
  learnerId: number,
  companyData: CompanyResponse | null | undefined
) => {
  if (!companyData?.cohorts) return [];

  return companyData.cohorts.filter((cohort) =>
    cohort.learners.some((learner) => learner.learnerId === learnerId)
  );
};

// Create columns function that takes company data as parameter
const createColumns = (companyData: CompanyResponse | null | undefined) => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        isChecked={table.getIsAllRowsSelected()}
        isIndeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        isChecked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    size: 50,
    meta: {
      isCentered: true,
      styles: {
        minWidth: '50px',
        maxWidth: '50px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <>
          <div
            className={`learner-${row.original.id}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.original.name ?? '-'}
          </div>
          <SmallPortalTooltip anchorSelect={`.learner-${row.original.id}`}>
            <TooltipContent>
              <TooltipDetail>{row.original.email}</TooltipDetail>
              {row.original.onboardingAnswers.role && (
                <TooltipDetail>
                  Role: {row.original.onboardingAnswers.role}
                </TooltipDetail>
              )}
            </TooltipContent>
          </SmallPortalTooltip>
        </>
      );
    },
    size: 200,
    meta: {
      styles: {
        minWidth: '200px',
        maxWidth: '200px',
      },
    },
  }),
  columnHelper.accessor('hasAccess', {
    header: 'Invited?',
    cell: ({ row }) => (row.original.hasAccess ? <CheckIcon /> : <CrossIcon />),
    meta: {
      isCentered: true,
      styles: {
        minWidth: '100px',
        maxWidth: '100px',
      },
    },
  }),
  columnHelper.display({
    id: 'learning-plan',
    header: 'Learning Plan',
    cell: ({ row }) => (
      <LearningPlanCell
        learnerId={row.original.id}
        learningPlan={row.original.learningPlan}
      />
    ),
    meta: {
      styles: {
        display: 'flex',
        minWidth: '700px',
        maxWidth: '700px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
      },
    },
  }),
  columnHelper.display({
    id: 'cohorts',
    header: 'Cohort',
    cell: ({ row }) => {
      const cohorts = getCohortsForLearner(row.original.id, companyData);

      if (cohorts.length === 0) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>None</span>;
      }

      const latestCohort = cohorts[0];

      const cohortText = `${day(latestCohort.startDate)
        .utc()
        .format('MMM DD')} - ${day(latestCohort.endDate)
        .utc()
        .format('MMM DD')}`;

      return (
        <>
          <div
            className={`cohorts-${row.original.id}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '14px',
            }}
          >
            {cohortText}
          </div>
          <SmallPortalTooltip anchorSelect={`.cohorts-${row.original.id}`}>
            <TooltipContent>
              <TooltipTitle>All Cohorts</TooltipTitle>
              {cohorts.map((cohort) => (
                <TooltipDetail>
                  {`${day(cohort.startDate)
                    .utc()
                    .format('MMM DD, YYYY')} - ${day(cohort.endDate)
                    .utc()
                    .format('MMM DD, YYYY')}${
                    cohort.isOpen ? ' (Active)' : ''
                  }`}
                </TooltipDetail>
              ))}
            </TooltipContent>
          </SmallPortalTooltip>
        </>
      );
    },

    meta: {
      styles: {
        minWidth: '200px',
        maxWidth: '200px',
      },
    },
  }),

  columnHelper.accessor('addedAt', {
    header: 'Added At',
    cell: (props) => {
      const id = `added-at-${props.row.original.id}`;

      if (!props.getValue()) {
        return <span className={id}>-</span>;
      }
      const visibleDate = dayjs.utc(props.getValue()).format('YYYY-MM-DD');

      return (
        <>
          <div data-tooltip-id={id}>{visibleDate}</div>
          <SmallTooltip anchorSelect={`[data-tooltip-id="${id}"]`}>
            <TooltipContent>
              <TooltipTitle>Added At</TooltipTitle>
              <TooltipDetail>
                {dayjs.utc(props.getValue()).format('YYYY-MM-DD HH:mm')}
              </TooltipDetail>
              <TooltipDetail>
                {dayjs.utc(props.getValue()).fromNow()}
              </TooltipDetail>
            </TooltipContent>
          </SmallTooltip>
        </>
      );
    },
    meta: {
      styles: {
        minWidth: '140px',
      },
    },
  }),
];

const TableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  margin-top: 8px;
  max-height: 73vh;
  > div {
    max-height: 73vh;
    ${({ enableVirtualization }) =>
      enableVirtualization &&
      css`
        height: 73vh;
      `}
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.bgActive};
    > div {
      border-radius: 8px;
    }
  }
  .learners-table {
    th {
      border-top: none !important;
    }
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
    }

    th {
      /* border-radius: 0 !important; */
    }
  }
`;
